import React from 'react'
import {Layer} from 'grommet'
import styled from 'styled-components'
import Icons from '../../../components/Icons'
import {_L, useLocaleState} from '../../../context/LocaleContext'
import MediaQuery from 'react-responsive'
import Image from '../../../containers/Detail/Image/Image'
import Occupant from './Occupant/Occupant'

const RoomSelectDetail = ({room, onSelectLocation, onClose}) => {
  const localeState = useLocaleState()
  const {rmId, name, rmTypeDesc, dpName, url, employees} = room
  const title =
    rmId + (name ? ' - ' + name : rmTypeDesc ? ' - ' + rmTypeDesc : '')
  const noneTxt = _L({key: 'None', localeState})
  const department = dpName || noneTxt
  return (
    <MediaQuery maxWidth={768}>
      {matches => (
        <Details
          position={matches ? 'bottom' : 'right'}
          modal={false}
          full={matches ? 'horizontal' : 'vertical'}
          margin={matches ? 'none' : {top: '80px'}}
          responsive={false}
        >
          <Container hasImage={!!url}>
            <CloseIcon onClick={onClose}>
              <Icons icon="CLOSE" height={14} width={14} />
            </CloseIcon>
            {url && (
              <StyledImageContainer>
                <Image url={url} />
              </StyledImageContainer>
            )}
            <StyledTitle>
              <StyledName>{title}</StyledName>
              <StyledTitleText>
                <StyledTitleTextLabel>
                  {_L({key: 'Department:', localeState})}
                </StyledTitleTextLabel>
                <StyledTitleTextValue>{department}</StyledTitleTextValue>
              </StyledTitleText>
            </StyledTitle>
            {!matches && (
              <StyleOccupants>
                <StyledTitleText>
                  <StyledTitleTextLabel>
                    {_L({key: 'Occupants:', localeState})}
                  </StyledTitleTextLabel>
                  {employees.length === 0 && (
                    <StyledTitleTextValue>{noneTxt}</StyledTitleTextValue>
                  )}
                </StyledTitleText>
                {employees.length > 0 && (
                  <ListContainer>
                    {employees.map((employee, index) => (
                      <Occupant
                        key={employee.emId + index}
                        employee={employee}
                      />
                    ))}
                  </ListContainer>
                )}
              </StyleOccupants>
            )}
            <StyledButtonContainer>
              <StyledButton onClick={onSelectLocation}>
                {_L({key: 'Select Location', localeState})}
              </StyledButton>
            </StyledButtonContainer>
          </Container>
        </Details>
      )}
    </MediaQuery>
  )
}

export default RoomSelectDetail

const Details = styled(Layer)`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: ${props =>
    props.full === 'vertical' ? 'calc(100vh - 80px);' : 'auto;'};

  grid-row: 1 / 3;
  grid-column: 3;

  @media (max-width: 768px) {
    grid-row: 1;
    grid-column: 1;
  }
`

const Container = styled.div`
  min-height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: ${props =>
    props.hasImage
      ? '19% 60px min-content min-content;'
      : '5% 60px min-content min-content;'} 
  grid-template-columns: 320px;
  grid-template-areas: 'image' 'title' 'occupants' 'button';
  box-sizing: border-box;
  background-color: white;

  @media (max-width: 1024px) {
    grid-template-rows:  ${props =>
      props.hasImage
        ? '15% 60px min-content min-content;'
        : '5% 60px min-content min-content;'} 
    grid-template-columns: 300px;
  }
  
   @media (max-width: 1024px) and (orientation : landscape) {
    grid-template-rows:  ${props =>
      props.hasImage
        ? '29% 60px min-content min-content;'
        : '5% 60px min-content min-content;'} 
    grid-template-columns: 300px;
   }

  @media (max-width: 768px) {
    height: 150px;
    width: 100%;
    grid-template-rows: 75px 75px;
    grid-template-columns:  ${props =>
      props.hasImage ? '30% 45%;' : '0% 75%;'} 
    grid-template-areas: 'image title . ' 'image title  button';
  }

  @media (max-width: 420px) {
    height: 192px;
    width: 100%;
    grid-template-rows: 50%;
    grid-template-columns: auto;
    grid-template-areas: 'title' 'button';
  }
`

const StyledImageContainer = styled.div`
  grid-area: image;
  padding: 0;
  box-sizing: border-box;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 420px) {
    display: none;
  }
`

const StyledTitle = styled.div`
  grid-area: title;
  box-sizing: border-box;
  margin: 0;
  padding: 19px 19px 19px 24px;
  text-align: left;

  @media (max-width: 768px) {
    margin: 19px 0 0 0;
    padding: 0 0 0 24px;
    border-bottom: 0;
    text-align: left;
  }

  @media (max-width: 420px) {
    margin: 0;
    padding: 45px 14px 14px 14px;
    border-right: 0;
    text-align: center;
  }
`

const StyledText = styled.div`
  font-size: 14px;
  font-weight: normal;
  letter-spacing: -0.3px;
`

const StyledName = styled(StyledText)`
  font-size: 18px;
  font-weight: bold;
  color: #000000;
  border-bottom: 1px solid #f3f5fb;
  padding-bottom: 11px;

  @media (max-width: 420px) {
    font-size: 16px;
    padding-bottom: 0;
  }
`

const StyledTitleText = styled(StyledText)`
  padding-top: 11px;

  @media (max-width: 420px) {
    font-size: 12px;
    padding-top: 0;
  }
`

const StyledTitleTextLabel = styled.span`
  color: #a4a9b7;
  float: left;
`

const StyledTitleTextValue = styled.span`
  float: right;
`

const StyleOccupants = styled.div`
  grid-area: occupants;
  box-sizing: border-box;
  margin: 0;
  padding: 19px 19px 19px 24px;
`

const ListContainer = styled.div`
  background-color: white;
  box-sizing: border-box;
  border-radius: 0 0 5px 5px;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  max-height: 375px;
`

const StyledButtonContainer = styled.div`
  grid-area: button;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
`

const StyledButton = styled.button`
  width: 85%;
  height: 48px;
  border-radius: 6px;
  background-color: #3395fe;
  border: 2px solid #3395fe;
  font-size: 16px;
  text-align: center;
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;
`

const CloseIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 38px;
  width: 38px;
  border-radius: 3px;
  background-color: #f4f5fa;
  cursor: pointer;
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 25;
`
